/**
 * Eaze plan prices in the visitor's / business's own currency.
 *
 * aeTech sets a fixed price list per currency (GHS, and NGN / KES / ZAR /
 * USD where a Paystack account takes them). The resolver mirrors the
 * backend (subscriptions/pricing.js):
 *
 *   billing currency = their own currency when Eaze can charge it, else
 *   USD when it can, else GHS.
 *   Price set in that currency → that exact price is what they pay.
 *   Not set → they pay the GHS price; we show "≈ their money" beside it.
 *
 *   usePlanPricing(override?)  → { local, billing, chargeable, plans, price(plan, cycle) }
 *   <PlanPrice plan={plan} cycle="monthly" />
 *
 * Signed in: the open business's currency. Signed out: the visitor's
 * country (lib/visitor-locale), or the country picked on sign-up.
 */
import React from "react";
import type { BillingCycle } from "@/constants/plans";
import { currencyPlural, formatPlanPrice, type PricedPlan } from "@/lib/plan-pricing";
import { usePlanPricing } from "@/hooks/usePlanPricing";

export const PlanPrice: React.FC<{
  plan: PricedPlan;
  cycle: BillingCycle;
  size?: "lg" | "md" | "sm";
  className?: string;
  /** Dark card (white text). */
  inverse?: boolean;
}> = ({ plan, cycle, size = "md", className = "", inverse = false }) => {
  const { price } = usePlanPricing();
  const p = price(plan, cycle);
  const per = cycle === "monthly" ? "mo" : "yr";
  const big = size === "lg" ? "text-4xl" : size === "md" ? "text-xl" : "text-sm";
  const main = inverse ? "text-white" : "text-gray-900 dark:text-foreground";
  const muted = inverse ? "text-white/60" : "text-gray-500 dark:text-muted-foreground";
  return (
    <span className={`inline-flex flex-col ${className}`}>
      <span className="inline-flex items-baseline gap-1">
        <span className={`${big} font-bold tabular-nums ${main}`}>{formatPlanPrice(p)}</span>
        <span className={`text-sm ${muted}`}>/{per}</span>
      </span>
      {p.estimate && (
        <span className={`text-[11px] ${muted}`}>
          {p.estimate} — charged in {currencyPlural(p.currency)}; your bank converts
        </span>
      )}
    </span>
  );
};

export default PlanPrice;

/** One line for billing screens when they're charged in a currency other than their own. */
export const PlanCurrencyNote: React.FC<{ className?: string; currency?: string | null }> = ({ className = "", currency }) => {
  const { local, billing, plans } = usePlanPricing();
  if (local === "GHS") return null;
  // Charged in: what the server says (a quote / their plan), else what the price list implies.
  const charged = currency || (billing !== "GHS" && plans.some((p) => !p.isEnterprise && p.prices?.[billing]?.monthly) ? billing : "GHS");
  if (charged === local) return null;
  return (
    <p className={`rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground ${className}`}>
      Eaze plans for your country are charged in {currencyPlural(charged)}. Your bank or mobile money converts at its own rate; amounts marked ≈ are estimates in your money.
    </p>
  );
};
